import { getFileBlobUrl } from '../../../services/storage-service';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useEffect, useState } from 'react';
import { useConfig } from 'hooks/use-config';
import { useTranslation } from 'react-i18next';

interface ImagePreviewProps {
  itemId: string;
  className?: string;
  alt?: string;
}

export const ImagePreview = ({ itemId, className, alt }: ImagePreviewProps) => {
  const { t } = useTranslation();
  const itemIdFilePathMap = useChatStore((state) => state.itemIdFilePathMap);
  const setItemIdFilePathMap = useChatStore((state) => state.setItemIdFilePathMap);
  const [isLoading, setIsLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const { configurationSettings } = useConfig();

  useEffect(() => {
    const fetchImage = async () => {
      if (!itemId || !configurationSettings) return;

      const filePath = itemIdFilePathMap.get(itemId);
      if (filePath) {
        setImageSrc(filePath);
        return;
      }

      setIsLoading(true);
      try {
        const blobUrl = await getFileBlobUrl(itemId, configurationSettings, undefined);
        if (blobUrl) {
          setImageSrc(blobUrl);
          setItemIdFilePathMap(itemId, blobUrl);
        }
      } catch (error) {
        // Error occurred, imageSrc will remain empty which will show "No image found"
        console.error('Failed to fetch image:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImage();
  }, [configurationSettings, itemId, itemIdFilePathMap, setItemIdFilePathMap]);

  if (isLoading) {
    return className ? <div className={className} aria-label={t('LOADING_IMAGE', { defaultValue: 'Loading image' })} /> : <div>{t('LOADING_IMAGE', { defaultValue: 'Loading image' })}</div>;
  }

  if (!imageSrc) {
    return className ? <div className={className} aria-label={t('NO_IMAGE_FOUND', { defaultValue: 'No image found' })} /> : <div>{t('NO_IMAGE_FOUND', { defaultValue: 'No image found' })}</div>;
  }

  return (
    <img
      className={className ?? 'h-48 w-96 object-contain'}
      src={imageSrc}
      alt={alt ?? ''}
      data-file-id={itemId}
    />
  );
};
